import React from 'react';
import { Box, Container, Typography, Grid, Avatar } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import StorageIcon from '@mui/icons-material/Storage';
import { ThaiStar, ThaiQuatrefoil, ThaiDivider } from './ThaiMotif';

const highlights = [
  {
    icon: <CodeIcon sx={{ fontSize: 26 }} />,
    title: 'Full-Stack Development',
    description: 'End-to-end web and API development with React, TypeScript, Golang, and more.',
    accent: '#C8A24D',
  },
  {
    icon: <PhoneAndroidIcon sx={{ fontSize: 26 }} />,
    title: 'Mobile Development',
    description: 'Cross-platform mobile apps using Flutter/Dart with clean architecture.',
    accent: '#5C3D2E',
  },
  {
    icon: <DataObjectIcon sx={{ fontSize: 26 }} />,
    title: 'Data Science & ML',
    description: 'Deep learning, NLP, time series forecasting, and MLOps pipeline engineering.',
    accent: '#6B8E5A',
  },
  {
    icon: <StorageIcon sx={{ fontSize: 26 }} />,
    title: 'Data Engineering',
    description: 'ETL/ELT pipelines, real-time data processing, and database optimization.',
    accent: '#B08E3A',
  },
];

const About: React.FC = () => {
  return (
    <Box
      id="about"
      sx={{
        py: { xs: 10, md: 14 },
        background: '#FFFFFF',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Thai motif decorations */}
      <ThaiStar size={160} opacity={0.035} rotate={12} sx={{ top: '5%', right: '3%' }} />
      <ThaiQuatrefoil size={80} opacity={0.04} strokeOnly sx={{ bottom: '10%', left: '3%' }} />
      <ThaiStar size={40} opacity={0.03} rotate={-8} sx={{ top: '40%', left: '6%' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: '#C8A24D',
              fontWeight: 600,
              letterSpacing: 3,
              fontSize: '0.8rem',
              mb: 1.5,
              display: 'block',
            }}
          >
            ABOUT ME
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              mb: 1,
              color: '#2A231C',
            }}
          >
            Building the{' '}
            <Box component="span" className="serif-heading" sx={{ color: '#C8A24D' }}>
              Future
            </Box>
            {' '}with Code
          </Typography>
          <ThaiDivider />
          <Typography
            variant="body1"
            sx={{
              color: '#7A6E60',
              maxWidth: 640,
              mx: 'auto',
              fontSize: { xs: '0.95rem', md: '1.05rem' },
            }}
          >
            I'm a software engineer based in Bangkok, Thailand — passionate about crafting scalable systems, 
            intelligent applications, and beautiful user experiences. With expertise spanning from low-level 
            data pipelines to polished frontend interfaces, I bring a holistic approach to every project.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {highlights.map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Box
                className="card"
                sx={{
                  p: 3.5,
                  height: '100%',
                  cursor: 'default',
                }}
              >
                <Avatar
                  sx={{
                    width: 52,
                    height: 52,
                    mb: 2.5,
                    background: `${item.accent}12`,
                    color: item.accent,
                  }}
                >
                  {item.icon}
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1.5,
                    fontSize: '1rem',
                    color: '#2A231C',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#7A6E60', fontSize: '0.9rem' }}
                >
                  {item.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default About;
